/**
 * Plot Dimension Validation
 *
 * This module validates that plot dimensions can physically fit within field boundaries
 * and provides helpful error messages and suggestions when they don't. It uses the same
 * positioning algorithms as the actual plot placement to ensure validation accuracy.
 *
 * Key responsibilities:
 * - Validate plot width and length against field boundaries
 * - Check that field buffers don't consume the entire field
 * - Validate that plot buffers don't consume entire plots
 * - Provide specific error messages for each validation failure
 * - Suggest maximum dimensions when validation fails
 * - Handle edge cases like irregular field shapes and buffer failures
 *
 * Used by: visualizationBuilder.ts, TestAreasVisualizer.tsx
 */

import * as turf from '@turf/turf';
import { convertBufferDimensions } from './coordinateConversions';
import {
  parseUniqueTreatments,
  findReplicatePosition,
} from './plotVisualization';

// Validation function to check if plot dimensions fit in the usable area
// Uses the same logic as plot placement for accuracy
export function validatePlotDimensions(
  usableArea: any,
  plotWidthM: number,
  plotLengthM: number,
  metersToDegreesLng: number,
  metersToDegreesLat: number,
  plotWidthFt: number,
  plotLengthFt: number,
  plotBufferWidthFt: number,
  plotBufferLengthFt: number,
  treatmentRates?: string,
  nominalRate?: number,
  fieldBufferFt?: number,
  ablineBearing?: number | null,
  ablineGeoJSON?: any,
  isPlot: boolean = true,
): {
  plotWidthValid: boolean;
  plotLengthValid: boolean;
  plotWidthError?: string;
  plotLengthError?: string;
  bufferValid: boolean;
  bufferError?: string;
  plotBufferValid: boolean;
  plotBufferError?: string;
  plotBufferWidthValid: boolean;
  plotBufferWidthError?: string;
  plotBufferLengthValid: boolean;
  plotBufferLengthError?: string;
} {
  // Calculate replicate dimensions first
  let numTreatments = 1;
  if (
    treatmentRates &&
    nominalRate !== undefined &&
    treatmentRates.trim() !== ''
  ) {
    try {
      const treatments = parseUniqueTreatments(treatmentRates, nominalRate);
      numTreatments = Math.max(1, treatments.length);
    } catch (err) {
      numTreatments = 1; // Fallback if parsing fails
    }
  }

  // Test if we can place a full replicate (different logic for plots vs strips)
  let canPlaceReplicate = false;
  const replicateWidthM = plotWidthM * numTreatments;
  const replicateLengthM = plotLengthM;

  if (usableArea && usableArea.geometry) {
    if (isPlot) {
      // For plots: Use the existing replicate positioning logic
      const replicateWidthDeg = replicateWidthM * metersToDegreesLng;
      const replicateLengthDeg = replicateLengthM * metersToDegreesLat;

      const { fitsInField } = findReplicatePosition({
        usableArea,
        replicateWidthDeg,
        replicateLengthDeg,
        ablineBearing,
        ablineGeoJSON,
      });

      canPlaceReplicate = fitsInField;
    } else {
      // For strips: Simple width check - do all treatments fit across the field width?
      const bbox = turf.bbox(usableArea);
      const usableWidthDeg = bbox[2] - bbox[0]; // maxLng - minLng
      const usableWidthM = usableWidthDeg / metersToDegreesLng;
      const totalWidthNeeded = replicateWidthM; // All treatments side by side

      canPlaceReplicate = totalWidthNeeded <= usableWidthM;
    }
  }

  let plotWidthValid = true;
  let plotLengthValid = true;
  let plotWidthError: string | undefined;
  let plotLengthError: string | undefined;
  let bufferValid = true;
  let bufferError: string | undefined;
  let plotBufferValid = true;
  let plotBufferError: string | undefined;

  // Check if field buffer consumes the whole field
  if (!usableArea || usableArea.geometry === null) {
    bufferValid = false;
    bufferError =
      'Field buffer too large - it consumes the entire field, leaving no usable area.';
  } else {
    try {
      const usableAreaSize = turf.area(usableArea);
      if (usableAreaSize <= 0) {
        bufferValid = false;
        bufferError =
          'Field buffer too large - it consumes the entire field, leaving no usable area.';
      }
    } catch (err) {
      bufferValid = false;
      bufferError =
        'Field buffer too large - it consumes the entire field, leaving no usable area.';
    }
  }

  // Check if plot/strip buffers consume too much of the plot/strip
  let plotBufferWidthValid = true;
  let plotBufferWidthError: string | undefined;
  let plotBufferLengthValid = true;
  let plotBufferLengthError: string | undefined;

  // Both plots and strips need width buffer validation
  const bufferDimensions = convertBufferDimensions(
    plotBufferWidthFt,
    plotBufferLengthFt,
  );
  const centerWidth = plotWidthM - 2 * bufferDimensions.widthM;

  // Check width buffers for both plots and strips
  if (centerWidth <= 0) {
    plotBufferWidthValid = false;
    plotBufferWidthError = `Width buffers consume the entire ${
      isPlot ? 'plot' : 'strip'
    } width, leaving no usable area.`;
  } else {
    const widthUsablePercent = (centerWidth / plotWidthM) * 100;
    if (widthUsablePercent < 10) {
      plotBufferWidthValid = false;
      plotBufferWidthError = `Width buffers too large - only ${widthUsablePercent.toFixed(
        1,
      )}% of ${
        isPlot ? 'plot' : 'strip'
      } width remains usable. Consider reducing width buffer size.`;
    }
  }

  // Check length buffers only for plots (strips don't have length buffers)
  if (isPlot) {
    const centerLength = plotLengthM - 2 * bufferDimensions.lengthM;

    if (centerLength <= 0) {
      plotBufferLengthValid = false;
      plotBufferLengthError =
        'Length buffers consume the entire plot length, leaving no usable area.';
    } else {
      const lengthUsablePercent = (centerLength / plotLengthM) * 100;
      if (lengthUsablePercent < 10) {
        plotBufferLengthValid = false;
        plotBufferLengthError = `Length buffers too large - only ${lengthUsablePercent.toFixed(
          1,
        )}% of plot length remains usable. Consider reducing length buffer size.`;
      }
    }
  }

  // Overall plot buffer validation logic
  if (isPlot) {
    // For plots: validation fails if either dimension fails
    plotBufferValid = plotBufferWidthValid && plotBufferLengthValid;

    // Combine error messages if both have issues
    if (!plotBufferWidthValid && !plotBufferLengthValid) {
      plotBufferError = `${plotBufferWidthError} ${plotBufferLengthError}`;
    } else if (!plotBufferWidthValid) {
      plotBufferError = plotBufferWidthError;
    } else if (!plotBufferLengthValid) {
      plotBufferError = plotBufferLengthError;
    }
  } else {
    // For strips: validation only depends on width buffers
    plotBufferValid = plotBufferWidthValid;
    plotBufferError = plotBufferWidthError;
  }

  if (!canPlaceReplicate && usableArea && usableArea.geometry) {
    if (isPlot) {
      // For plots: Use the existing diagnostic logic
      const testWithSmallerWidth = findReplicatePosition({
        usableArea,
        replicateWidthDeg: replicateWidthM * 0.5 * metersToDegreesLng,
        replicateLengthDeg: replicateLengthM * metersToDegreesLat,
        ablineBearing,
        ablineGeoJSON,
      });

      const testWithSmallerLength = findReplicatePosition({
        usableArea,
        replicateWidthDeg: replicateWidthM * metersToDegreesLng,
        replicateLengthDeg: replicateLengthM * 0.5 * metersToDegreesLat,
        ablineBearing,
        ablineGeoJSON,
      });

      // Test width while keeping original length
      if (testWithSmallerWidth.fitsInField) {
        plotWidthValid = false;
        plotWidthError = `Plot width is too large for this area. Please reduce the plot width.`;
      }

      // Test length while keeping original width
      if (testWithSmallerLength.fitsInField) {
        plotLengthValid = false;
        plotLengthError = `Plot length is too large for this area. Please reduce the plot length.`;
      }

      // If neither smaller version fits, determine which dimension(s) are problematic
      if (
        !testWithSmallerWidth.fitsInField &&
        !testWithSmallerLength.fitsInField
      ) {
        // Test with both dimensions reduced to see if the area can fit any reasonable plot
        const testBothSmaller = findReplicatePosition({
          usableArea,
          replicateWidthDeg: replicateWidthM * 0.5 * metersToDegreesLng,
          replicateLengthDeg: replicateLengthM * 0.5 * metersToDegreesLat,
          ablineBearing,
          ablineGeoJSON,
        });

        if (testBothSmaller.fitsInField) {
          // Area can fit smaller plots, so both current dimensions are too big
          plotWidthValid = false;
          plotLengthValid = false;
          plotWidthError = `Plot width is much too large for this area. Please significantly reduce the plot width.`;
          plotLengthError = `Plot length is much too large for this area. Please significantly reduce the plot length.`;
        } else {
          // Even half-size doesn't fit - area is very constrained, blame the larger dimension
          plotWidthValid = false;
          plotWidthError = `Plot dimensions are too large for this constrained area. Try significantly smaller dimensions.`;
        }
      }
    } else {
      // For strips: Width is the only thing that can be wrong (length spans full field)
      plotWidthValid = false;
      if (numTreatments > 1) {
        plotWidthError = `Strip width is too large for us to fit all treatments across this field. Please reduce the strip width until all treatments fit.`;
      } else {
        plotWidthError = `Strip width is too large for this field. Please reduce the strip width.`;
      }
    }
  }

  return {
    plotWidthValid,
    plotLengthValid,
    plotWidthError,
    plotLengthError,
    bufferValid,
    bufferError,
    plotBufferValid,
    plotBufferError,
    plotBufferWidthValid,
    plotBufferWidthError,
    plotBufferLengthValid,
    plotBufferLengthError,
  };
}
